import { useState, useEffect, useCallback } from 'react';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Group } from '@/types/group';

interface UseGroupsReturn {
  groups: Group[];
  loading: boolean;
  error: string | null;
  createGroup: (name: string, description: string, members: string[]) => Promise<string>;
  updateGroup: (id: string, data: Partial<Group>) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  addMember: (groupId: string, userId: string) => Promise<void>;
  removeMember: (groupId: string, userId: string) => Promise<void>;
}

export const useGroups = (): UseGroupsReturn => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for groups
  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      const q = query(collection(db, 'groups'), orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const groupList = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.name || '',
              description: data.description || '',
              members: data.members || [],
              createdBy: data.createdBy || '',
              createdAt: data.createdAt || '',
              updatedAt: data.updatedAt || '',
            } as Group;
          });
          setGroups(groupList);
          setLoading(false);
        },
        (err) => {
          console.error('Groups listener error:', err);
          setError(err.message);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set up groups listener');
      setLoading(false);
      return () => {};
    }
  }, []);

  const createGroup = useCallback(async (name: string, description: string, members: string[]): Promise<string> => {
    setError(null);
    try {
      const docRef = await addDoc(collection(db, 'groups'), {
        name,
        description,
        members,
        createdBy: 'current-user', // TODO: Get from auth context
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      return docRef.id;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create group';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const updateGroup = useCallback(async (id: string, data: Partial<Group>): Promise<void> => {
    setError(null);
    try {
      await updateDoc(doc(db, 'groups', id), {
        ...data,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update group';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const deleteGroup = useCallback(async (id: string): Promise<void> => {
    setError(null);
    try {
      await deleteDoc(doc(db, 'groups', id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete group';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const addMember = useCallback(
    async (groupId: string, userId: string): Promise<void> => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) throw new Error('Group not found');

      const members = [...group.members];
      if (!members.includes(userId)) {
        members.push(userId);
        await updateGroup(groupId, { members });
      }
    },
    [groups, updateGroup],
  );

  const removeMember = useCallback(
    async (groupId: string, userId: string): Promise<void> => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) throw new Error('Group not found');

      const members = group.members.filter((id) => id !== userId);
      await updateGroup(groupId, { members });
    },
    [groups, updateGroup],
  );

  return {
    groups,
    loading,
    error,
    createGroup,
    updateGroup,
    deleteGroup,
    addMember,
    removeMember,
  };
};
